(ns admin.components.composite.curriculum.activity-list
  (:require
   [clojure.string :as str]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [kyber.components.base.badge :as badge]
   [kyber.components.base.text :as text]
   [kyber.components.base.icons :as icon]
   [kyber.components.base.link :as link]
   [kyber.styles.core :refer [gs styles->classes]]
   [kyber.utils :as ku]
   [kyber.lib.kyber-db :as kyber-db]
   [admin.utils :as au]
   [admin.router.utils :as aru]
   [admin.libs.flip-move :refer [FlipMove]]
   [admin.styles.constants :refer [activity-card-tile-min-height
                                   activity-card-tile-width]]
   [potpuri.core :as pot]))

(def classes
  (styles->classes
   {:title
    {:margin-bottom (gs [:spacing :p12])
     :padding-bottom (gs [:spacing :p12])
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-200]))}

    :sub-title
    {:margin-bottom (gs [:spacing :p20])}

    :activity-cards-container
    {:display :flex
     :flex-direction :column}

    :activity-card-container
    {:border (str "1px solid " (gs [:colors :gray-scale-200]))
     :border-radius (gs [:radius :r7])
     :overflow :hidden
     :margin-bottom (gs [:spacing :p24])
     :width "100%"
     :display :flex
     :flex-direction :row
     :position :relative}

    :is-hovering-card
    {:transform "rotate(-0.5deg) !important"
     :border-radius (gs [:radius :r7])
     :border (str "1px solid " (gs [:colors :gray-scale-500])
                  " !important")
     :box-shadow (str (gs [:shadows :shadow-24]) "!important")}

    :can-sort-card
    {:cursor :grab}

    :is-updating-card-sort
    {:cursor "wait !important"
     "& > .overlay"
     {:position :absolute
      :top 0
      :left 0
      :width "100%"
      :height "100%"
      :background-color "rgba(0,0,0,0.1)"}}

    :new-activity-box-container
    {:display :flex
     :align-items :center
     :flex-direction :column
     :justify-content :center
     :border (str "1px solid " (gs [:colors :gray-scale-200]))
     :border-radius (gs [:radius :r7])
     :background-color (gs [:colors :gray-scale-100])
     :margin-bottom (gs [:spacing :p24])
     :width "100%"
     :padding (gs [:spacing :p20])
     :min-height "400px"}

    :new-activity-box-title
    {:text-align :center
     :margin-bottom (gs [:spacing :p20])}

    :activity-card-body-container
    {:width "100%"}

    :activity-card-metadata-container
    {:padding [(gs [:spacing :p8])
               (gs [:spacing :p8])
               (gs [:spacing :p8])
               (gs [:spacing :p20])]
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))
     :display :flex
     :align-items :center
     :justify-content :space-between}

    :activity-poster-container
    {:min-height (str activity-card-tile-min-height "px")
     :min-width (str activity-card-tile-width "px")
     :width (str activity-card-tile-width "px")
     :display :flex
     :align-items :center
     :justify-content :center}

    :activity-details-container
    {:padding (gs [:spacing :p20])
     :height "100%"}

    :activity-cta-container
    {:display :flex
     :flex-direction :column
     :align-items :center
     :padding (gs [:spacing :p20])
     :border-left (str "1px solid " (gs [:colors :gray-scale-100]))}

    :activity-primary-cta-container
    {:display :flex
     :flex-direction :column

     "& > *"
     {:width "100%"
      :margin-bottom (gs [:spacing :p12])}}}))

(defn- render-activity-metadata
  [{:keys [created-at updated-at published]}]
  [:div {:class (:activity-card-metadata-container classes)}
   [text/caption-20 :p
    (if (some? updated-at)
      (str "Last Edited: " (ku/date->day-string updated-at))
      (str "Created: " (ku/date->day-string created-at)))]
   [badge/render
    (if (true? published)
      {:variant :success :label "PUBLISHED"}
      {:variant :neutral :label "DRAFT"})]])

(defn- render-activity-poster-image
  [curriculum-uuid {tile :tile
                    activity-uuid :uuid}]
  [:div (cond-> {:class (:activity-poster-container classes)}
          (nil? tile)
          (assoc-in [:style :background-color] (gs [:colors :gray-scale-100]))

          (some? tile)
          (update :style merge {:background (str "url('" tile "')")
                                :background-repeat :no-repeat
                                :background-size :cover}))
   (when (nil? tile)
     [:div
      [text/title-20 :p
       {:style {:padding-top (gs [:spacing :p20])
                :padding-bottom (gs [:spacing :p20])
                :white-space :nowrap}}
       "No Preview Image"]
      [link/link {:href (aru/admin-path :activity-settings
                                        :curriculum-uuid curriculum-uuid
                                        :activity-uuid activity-uuid)
                  :style {:margin (gs [:spacing :p0])
                          :text-align :center
                          :display :block}}
       "Add Preview"]])])

(defn- render-activity-details
  [{activity-name :name
    activity-description :description}]
  [:div {:class (:activity-details-container classes)}
   [text/title-30 :p {:data-test "activity-list-activity-title"} activity-name]

   (when (some? activity-description)
     [text/caption-20 :p {:data-test "activity-list-activity-description"}
      activity-description])])

(defn- render-activity-score
  [_activity]
  ;; TODO Requires more data form the API.
  [:div])

(defn- render-activity-ctas
  [{:keys [curriculum-uuid]}]
  (let [can-view-insights
        (re-frame/subscribe [:authentication/has-access?
                             :activity/can-view-insights
                             {:curriculum-uuid curriculum-uuid}])

        can-view-director-report
        (re-frame/subscribe [:authentication/has-access?
                             :activity/can-view-director-report
                             {:curriculum-uuid curriculum-uuid}])

        can-edit-activity
        (re-frame/subscribe [:authentication/has-access?
                             :activity/can-edit
                             {:curriculum-uuid curriculum-uuid}])

        {:current-user/keys [curriculum-roles super-user?]}
        @(re-frame/subscribe [::kyber-db/get-record
                              {:model :current-user
                               :ident "current-user"}])

        no-external-access? (and (not super-user?)
                                 (or (contains? curriculum-roles :admin)
                                     (contains? curriculum-roles :content-manager)
                                     (contains? curriculum-roles :insights-viewer)))]

    (fn [{:keys [curriculum-uuid activity default-activity-route]}]
      (let [{activity-uuid :uuid external-activity-link :external-activity-link} activity]
        [:div {:class (:activity-cta-container classes)}
         (if (seq external-activity-link)
           [:div {:class (:activity-primary-cta-container classes)}
            [link/primary-anchor-button
             (cond-> {:href (aru/admin-path :activity-settings
                                            :curriculum-uuid curriculum-uuid
                                            :activity-uuid activity-uuid)}
               no-external-access?
               (assoc :class "disabled"))
             "View External Activity"]]

           [:div {:class (:activity-primary-cta-container classes)}
            (if @can-edit-activity
              [link/primary-anchor-button
               {:href (aru/admin-path default-activity-route
                                      :curriculum-uuid curriculum-uuid
                                      :activity-uuid activity-uuid)
                :data-test "activity-list-edit-activity-link"}
               "Activity Build"]
              (when @can-view-director-report
                [link/primary-anchor-button
                 {:href (aru/admin-path :reports-director
                                        :curriculum-uuid curriculum-uuid
                                        :activity-uuid activity-uuid)
                  :data-test "activity-list-view-reports-director-link"}
                 "View Manager Reports"]))

            (when (and @can-view-insights (:looker activity))
              [link/primary-anchor-button
               {:href (aru/admin-path :dashboard-list
                                      :curriculum-uuid curriculum-uuid
                                      :activity-uuid activity-uuid)
                :data-test "activity-list-view-analytics-link"}
               "View Analytics"])])

         [link/link {:href (if (and (some? external-activity-link)
                                    (seq external-activity-link))
                             external-activity-link
                             (au/->learner-url {:curriculum curriculum-uuid
                                                :activity activity-uuid}))
                     :target "_blank"}
          [:span {:style {:white-space :nowrap}}
           "Preview Activity"
           [icon/icon-sm {:style {:margin-left (gs [:spacing :p4])
                                  :margin-top (gs [:spacing :p20])
                                  :color (gs [:colors :primary-lite-600])}}
            :external-link]]]]))))

(defn- get-element-idx
  [element]
  (when (some? element)
    (let [data-idx (.getAttribute element "data-idx")]
      (if (some? data-idx)
        (js/parseInt data-idx)
        (get-element-idx (.-parentNode element))))))

(defn- set-hover-idx
  [{:keys [element-idx hover-idx]}]
  (when-not (= @hover-idx element-idx)
    (reset! hover-idx element-idx)))

(defn- render-activity-card
  [{:keys [hover-idx]}]
  (let [is-dragging (atom false)]
    (fn [{:keys [curriculum-uuid
                activity
                is-sortable
                on-sort-update
                is-updating-sort
                default-activity-route]}]
      (let [can-drag
            (if (true? is-updating-sort)
              false
              is-sortable)

            {:keys [idx]}
            activity]
        [:li {:class
              (cond-> [(:activity-card-container classes)]
                (and (false? @is-dragging)
                     (= @hover-idx idx)
                     (true? is-sortable))
                (conj (:is-hovering-card classes))

                (true? is-sortable)
                (conj (:can-sort-card classes))

                (and (true? is-sortable)
                     (true? is-updating-sort))
                (conj (:is-updating-card-sort classes)))

              :draggable
              can-drag

              :data-idx
              idx

              :on-drag-start
              #(do
                 (reset! is-dragging true)
                 (.setData (.-dataTransfer %) "text" idx))

              :on-drag-enter
              #(let [element-idx (get-element-idx (.-target %))]
                 (.preventDefault %)
                 (set-hover-idx {:hover-idx hover-idx
                                 :element-idx element-idx}))

              :on-drag-leave
              #(do
                 (.preventDefault %)
                 (set-hover-idx {:hover-idx hover-idx
                                 :element-idx nil}))

              :on-drag-over
              #(let [element-idx (get-element-idx (.-target %))]
                 (.preventDefault %)
                 (set-hover-idx {:hover-idx hover-idx
                                 :element-idx element-idx}))

              :on-drop
              #(let [end-idx (get-element-idx (.-target %))
                     start-idx (js/parseInt (.getData (.-dataTransfer %)
                                                      "text"))]
                 (.preventDefault %)
                 (reset! is-dragging false)
                 (reset! hover-idx nil)

                 (when (and (some? end-idx)
                            (some? start-idx)
                            (not= end-idx start-idx))
                   (on-sort-update {:start start-idx
                                    :end end-idx})))

              :on-drag-end
              #(do
                 (.preventDefault %)
                 (reset! is-dragging false))}
         [:div {:class "overlay"}]
         [render-activity-poster-image curriculum-uuid activity]
         [:div {:class (:activity-card-body-container classes)}
          [render-activity-metadata activity]
          [render-activity-details activity]
          [render-activity-score activity]]
         [render-activity-ctas (pot/map-of curriculum-uuid activity default-activity-route)]]))))

(defn render
  [_]
  (let [hover-idx (reagent/atom nil)]
    (fn [{:keys [curriculum-uuid
                activities
                title
                description
                on-sort-update
                is-updating-sort
                default-activity-route]}]
      [:div
       [text/title-40 :h3
        {:class (:title classes)}
        title]
       [text/caption-20 :p
        {:class (:sub-title classes)}
        (if (empty? activities)
          (str "You have no " (str/lower-case title) ".")
          description)]

       [(if (some? on-sort-update)
          FlipMove
          :ul)

        (cond-> {:class (:activity-cards-container classes)}
          (some? on-sort-update)
          (assoc :type-name "ul"))

        (for [{activity-uuid :uuid :as activity}
              (if (some? on-sort-update)
                (map-indexed #(assoc %2 :idx %1) activities)
                activities)]
          ^{:key activity-uuid}
          [render-activity-card {:curriculum-uuid curriculum-uuid
                                 :activity activity
                                 :default-activity-route default-activity-route
                                 :is-sortable (some? on-sort-update)
                                 :hover-idx hover-idx
                                 :on-sort-update on-sort-update
                                 :is-updating-sort is-updating-sort}])]])))
